//@flow

// Vendors
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { QRCodeSVG } from 'qrcode.react';

// Texkit Components
import { ButtonPrimary } from 'texkit/dist/components/Buttons';

// Components
import Modal from '../Modal';
import Trans from '../../Trans/Trans';

// Actions
import { closeModal, openModal } from '../../../../reducers/modal/modalActions';

// Requests
import {
  cancelScanSessionRequest,
  createScanSessionRequest,
  hasScanSessionError,
  pollScanResultsRequest,
} from '../../../../backend/dlScanRequests';
import {
  SCAN_STATUS,
  createScanTimestamp,
  createScanTransactionId,
  recordIdScanUsageRequest,
} from '../../../../backend/productUsageRequests';

// Helpers
import {
  trackBarcodeScanEvent,
  BARCODE_SCAN_EVENTS,
} from '../../../../lib/helpers/barcodeScanAnalytics';
import {
  formatRemaining,
  toLocalExpireAt,
} from '../../../../lib/helpers/dlScanSession';
import useCountdown from '../../../../hooks/useCountdown';

// Styles
import './dl-id-switch-to-phone-modal.scss';

type Props = {
  onCapture?: Function,
  onClose?: Function,
};

const MODAL_NAME = 'DLIDSwitchToPhoneModal';
const ERROR_MODAL_NAME = 'DLIDScanErrorModal';
const POLL_INTERVAL_MS = 5000;
// Keys are normalized: lowercase with underscores removed.
const POLL_STATUS_TO_SCAN_STATUS = {
  started: 'pending',
  inprogress: 'in_progress',
  completed: 'completed',
};

const toScanStatus = pollStatus =>
  POLL_STATUS_TO_SCAN_STATUS[
    String(pollStatus || '')
      .toLowerCase()
      .replace(/_/g, '')
  ];

const isPollError = (response, body) => {
  const httpStatus = response && response.status;
  return (
    httpStatus === 400 ||
    httpStatus >= 500 ||
    hasScanSessionError(body) ||
    String((body && body.scanStatus) || '').toUpperCase() === 'INVALID' ||
    !!(body && body.errors)
  );
};

const recordScanUsage = dispatch => {
  try {
    const action = recordIdScanUsageRequest(
      createScanTransactionId(),
      SCAN_STATUS.SUCCESS,
      createScanTimestamp()
    );
    Promise.resolve(dispatch(action)).catch(error => {
      const responseStatus = error && error.response && error.response.status;
      console.warn(
        'ID scan usage reporting failed',
        responseStatus || (error && error.message)
      );
    });
  } catch {
    console.warn('ID scan usage reporting failed');
  }
};

const parseScanDetails = scannedDetails => {
  if (!scannedDetails) return {};
  if (typeof scannedDetails === 'string') return JSON.parse(scannedDetails);
  return scannedDetails;
};
// Override for local dev so the phone can reach the app (e.g. an ngrok URL).
const PHONE_SCAN_ORIGIN =
  process.env.REACT_APP_DL_SCAN_PHONE_ORIGIN || window.location.origin;

const buildPhoneScanUrl = sessionId =>
  `${PHONE_SCAN_ORIGIN}/dl-scan/${encodeURIComponent(sessionId)}`;

const DLIDSwitchToPhoneModal = ({ onCapture, onClose }: Props) => {
  const dispatch = useDispatch();
  const isOpen = useSelector(({ modal }) => modal.activeModal === MODAL_NAME);
  const pathname = useSelector(({ router }) => router.location.pathname);
  const [scan, setScan] = useState({ status: 'loading', sessionId: null });
  const [expireAt, setExpireAt] = useState(null);
  const [wasOpen, setWasOpen] = useState(isOpen);
  // Reset during render so a previous session's QR never paints on reopen.
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setScan({ status: 'loading', sessionId: null });
      setExpireAt(null);
    }
  }
  // Mirrors the phone's countdown; at 0 the phone page (MobileDLIDScan) shows its generic "Something went wrong" state.
  const remainingMs = useCountdown(expireAt, isOpen);
  const onCaptureRef = useRef(onCapture);
  onCaptureRef.current = onCapture;
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  // Redux keeps the modal open across navigation (e.g. browser back), so close it when leaving.
  useEffect(
    () => () => {
      if (!isOpenRef.current) return;
      dispatch(closeModal(MODAL_NAME));
      if (document.body) document.body.classList.remove('modalActive');
    },
    [dispatch, pathname]
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    const controller = new AbortController();
    let pollTimeoutId;
    // Session that is still live on the server and should be cancelled on close.
    let activeSessionId = null;

    const handleError = () => {
      activeSessionId = null;
      if (!controller.signal.aborted) dispatch(openModal(ERROR_MODAL_NAME));
      dispatch(trackBarcodeScanEvent(BARCODE_SCAN_EVENTS.DESKTOP_FAILED));
    };

    const poll = async sessionId => {
      const { response, body } = await dispatch(
        pollScanResultsRequest(sessionId)
      );
      if (controller.signal.aborted) return;
      if (isPollError(response, body)) {
        throw new Error('DL scan status check failed');
      }
      const { scanStatus: pollStatus, scannedDetails } = body || {};
      const status = toScanStatus(pollStatus);
      if (!status) throw new Error(`Unexpected DL scan status: ${pollStatus}`);
      if (status === 'completed') {
        activeSessionId = null;
        recordScanUsage(dispatch);
        const capturedData = parseScanDetails(scannedDetails);
        if (onCaptureRef.current) onCaptureRef.current(capturedData);
        setScan({ status, sessionId });
        dispatch(trackBarcodeScanEvent(BARCODE_SCAN_EVENTS.DESKTOP_SUCCESS));
        return;
      }
      setScan({ status, sessionId });
      pollTimeoutId = setTimeout(
        () => poll(sessionId).catch(handleError),
        POLL_INTERVAL_MS
      );
    };

    const createSession = async () => {
      const { body } = await dispatch(createScanSessionRequest());
      if (controller.signal.aborted) return;
      const {
        session_id: sessionId,
        expires_at: expiresAt,
        generated_at: generatedAt,
      } = body || {};
      if (hasScanSessionError(body))
        throw new Error('DL scan session could not be created');
      if (!sessionId) throw new Error('DL scan session was not created');
      activeSessionId = sessionId;
      // Countdown is display-only; polling ends when the server reports INVALID or an error.
      setExpireAt(toLocalExpireAt(expiresAt, generatedAt));
      setScan({ status: 'pending', sessionId });
      await poll(sessionId);
    };

    setScan({ status: 'loading', sessionId: null });
    createSession().catch(handleError);

    return () => {
      controller.abort();
      clearTimeout(pollTimeoutId);
      if (activeSessionId) {
        Promise.resolve(
          dispatch(cancelScanSessionRequest(activeSessionId))
        ).catch(() => {});
      }
    };
  }, [dispatch, isOpen]);

  const handleManualEntry = () => {
    dispatch(closeModal(MODAL_NAME));
  };

  // Runs after the exit transition, so the success view stays visible while fading out.
  const handleClose = () => {
    setScan({ status: 'loading', sessionId: null });
    setExpireAt(null);
    if (onClose) onClose();
  };

  const renderScanArea = () => {
    if (scan.status === 'pending' && scan.sessionId) {
      return (
        <div className="dl-id-switch-to-phone-qr">
          <QRCodeSVG
            value={buildPhoneScanUrl(scan.sessionId)}
            size={304}
            level="M"
            role="img"
            aria-label="QR code to continue scanning on your phone"
          />
        </div>
      );
    }
    if (scan.status === 'in_progress') {
      return (
        <div
          className="dl-id-switch-to-phone-status"
          role="status"
          aria-live="polite"
        >
          <p className="dl-id-switch-to-phone-status-text">
            <Trans
              file="Labels"
              id="ScanInProgressOnMobileDevice"
              fallback="Scan in progress on your mobile device"
            />
          </p>
        </div>
      );
    }
    return (
      <div
        className="dl-id-switch-to-phone-status"
        role="status"
        aria-busy="true"
        aria-label="Loading QR code"
      />
    );
  };

  return (
    <Modal
      name={MODAL_NAME}
      className="dl-id-switch-to-phone-modal"
      onClose={handleClose}
      disableOnClickOutside
    >
      {scan.status === 'completed' ? (
        <>
          <h2 className="dl-id-switch-to-phone-title">
            <Trans
              file="Labels"
              id="ScanSuccessful"
              fallback="Scan successful!"
            />
          </h2>
          <p className="dl-id-switch-to-phone-description">
            <Trans
              file="Labels"
              id="ScanSuccessfulDescription"
              fallback="We've filled in your driver license or ID information. Review it and add your Social Security Number to continue."
            />
          </p>
          <svg
            className="dl-id-switch-to-phone-success-icon"
            viewBox="0 0 96 96"
            aria-hidden="true"
          >
            <circle cx="48" cy="48" r="44" />
            <path d="M30 50l12 12 24-26" />
          </svg>
          <ButtonPrimary
            type="button"
            className="dl-id-switch-to-phone-success-button"
            onClick={() => dispatch(closeModal(MODAL_NAME))}
          >
            <Trans file="Labels" id="okGotItConfirm" fallback="OK, got it" />
          </ButtonPrimary>
        </>
      ) : (
        <>
          <h2 className="dl-id-switch-to-phone-title">
            <Trans
              file="Labels"
              id="SwitchToSmartphoneToScanDLID"
              fallback="Switch to your smartphone to scan your driver license or ID"
            />
          </h2>
          <p className="dl-id-switch-to-phone-description">
            <Trans
              file="Labels"
              id="SwitchToSmartphoneToScanDLIDDescription"
              fallback="Use the camera on your phone to scan this QR code. Then scan your driver license or ID. When you're done scanning, come back here to finish creating your TxT account."
            />
          </p>
          {remainingMs !== null &&
            scan.status === 'pending' &&
            scan.sessionId && (
            <p className="dl-id-switch-to-phone-time-remaining" role="timer">
              <Trans
                file="Labels"
                id="ScanTimeRemaining"
                fallback="Time remaining: <strong>{{1}}</strong>"
                args={[formatRemaining(remainingMs)]}
              />
            </p>
          )}
          <p className="dl-id-switch-to-phone-keep-open">
            <Trans
              file="Labels"
              id="KeepWindowOpenWhileScanning"
              fallback="Keep this window open while you scan."
            />
          </p>
          {renderScanArea()}
          <button
            type="button"
            className="text-button dl-id-switch-to-phone-manual-entry"
            onClick={handleManualEntry}
          >
            <Trans
              file="Labels"
              id="EnterInformationManually"
              fallback="Enter information manually"
            />
          </button>
        </>
      )}
    </Modal>
  );
};

export default DLIDSwitchToPhoneModal;
